import { useEffect } from 'react';
import {
  FanguardApiContractsJobNotificationModelModel,
  FanguardApiContractsNotificationIndicatorEnum,
  FanguardApiContractsNotificationModel,
} from 'src/api/generated';
import { useSignalR } from 'src/hooks/useSignalR';
import {
  addNotification,
  clearAllNotifications,
  notificationsStore,
  clearNotificationById,
  findNotificationById,
  updateJobFailedNumber,
  updateJobSuccessNumber,
  updateJobMessages,
} from 'src/stores/notificationsStore';
import { useGetJob } from './api/useJobsEndpoint';
import useStore from 'src/stores/useStore';
import { t } from 'i18next';

const INTERACTION_CREATED = 'interactioncreated';
const ALERT_UPDATED = 'alertupdated';
const DEVICE_STATUS_CHANGED = 'devicestatuschanged';
const CROWD_COUNT_THRESHOLD_EXCEEDED = 'crowdcountthresholdexceeded';
const SENSOR_PAIRING_STATUS_CHANGED = 'sensorpairingstatuschanged';
const SENSOR_PAIRING_CHANGED = 'sensorpairingchanged';

// Job notifications
const JOB_MESSAGE = 'jobmessage';
const JOB_FAILED = 'jobfailed';
const JOB_COMPLETE = 'jobcomplete';

export const useNotifications = () => {
  const lastJob = useStore(notificationsStore, (state) => state.jobs[state.jobs.length - 1]);
  const jobMessages =
    useStore(notificationsStore, (state) => state.jobs.find((job) => job.jobId === lastJob?.jobId)?.messages) ?? [];

  const { data: jobs, isSuccess } = useGetJob(lastJob?.jobId);

  useSignalR({
    methodName: INTERACTION_CREATED,
    callback: (data: FanguardApiContractsNotificationModel) => {
      addNotification(data);
    },
  });

  useSignalR({
    methodName: ALERT_UPDATED,
    callback: (data: FanguardApiContractsNotificationModel) => {
      addNotification(data);
    },
  });

  useSignalR({
    methodName: DEVICE_STATUS_CHANGED,
    callback: (data: FanguardApiContractsNotificationModel) => {
      addNotification(data);
    },
  });

  useSignalR({
    methodName: CROWD_COUNT_THRESHOLD_EXCEEDED,
    callback: (data: FanguardApiContractsNotificationModel) => {
      addNotification(data);
    },
  });

  useSignalR({
    methodName: SENSOR_PAIRING_STATUS_CHANGED,
    callback: (data: FanguardApiContractsNotificationModel) => {
      addNotification(data);
    },
  });

  useSignalR({
    methodName: SENSOR_PAIRING_CHANGED,
    callback: (data: FanguardApiContractsNotificationModel) => {
      addNotification(data);
    },
  });

  // Job notifications
  useSignalR({
    methodName: JOB_MESSAGE,
    callback: (data: FanguardApiContractsJobNotificationModelModel) => {
      if (jobMessages.find((m) => m.message === data.message)) {
        return;
      }
      updateJobMessages(lastJob?.jobId, data);
      if (data.level === 'Error') {
        updateJobFailedNumber(lastJob?.jobId);
      }
      if (data.level === 'Success') {
        updateJobSuccessNumber(lastJob?.jobId);
      }
    },
  });

  useSignalR({
    methodName: JOB_FAILED,
    callback: (data: FanguardApiContractsJobNotificationModelModel) => {
      return data;
    },
  });

  useSignalR({
    methodName: JOB_COMPLETE,
    callback: (data: FanguardApiContractsJobNotificationModelModel) => {
      const notification = findNotificationById(lastJob?.jobId);
      const title = notification?.title.split(' ')[1];

      clearNotificationById(lastJob?.jobId);

      addNotification({
        id: lastJob?.jobId,
        message:
          jobs?.failedItems !== undefined && jobs?.failedItems > 0
            ? t('general.importFailed')
            : t('general.importSuccessful'),
        indicator:
          jobs?.failedItems !== undefined && jobs?.failedItems > 0
            ? FanguardApiContractsNotificationIndicatorEnum.Failure
            : FanguardApiContractsNotificationIndicatorEnum.Success,
        timestamp: new Date(),
        title:
          jobs?.failedItems !== undefined && jobs?.failedItems > 0
            ? `${title} ${t('general.error').toLowerCase()}`
            : `${title} ${t('general.imported').toLowerCase()}`,
        listName: notification?.listName,
      });
      return data;
    },
  });

  useEffect(() => {
    if (isSuccess && jobs?.messages) {
      for (const message of jobs.messages) {
        if (jobMessages.some((m) => m.message === message.message)) {
          continue;
        }
        updateJobMessages(lastJob?.jobId, message);
        if (message.level === 'Error') {
          updateJobFailedNumber(lastJob?.jobId);
        }
        if (message.level === 'Success') {
          updateJobSuccessNumber(lastJob?.jobId);
        }
      }
    }
  }, [isSuccess, jobs]);

  // Clear notifications on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      clearAllNotifications();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
};

export default useNotifications;
